import { useEffect, useMemo, useState } from 'react'

import { useUnit } from 'effector-react'
import { useQuery } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import { massMessageService } from '@/modules/online-mass-messages/service/mass-message-service.ts'
import { handleError } from '@/utils/error'

const _normalize = (value: number, max: number) => {
  return max === 0 ? 0 : (value / max) * 100
}

export const useWithdrawMassMessage = (messageId: number) => {
  const [progress, setProgress] = useState<number | undefined>(0)

  const creatorId = useUnit($creatorId)

  const withdrawMessage = useQuery(
    ['bulk-message', 'withdraw', messageId],
    () =>
      massMessageService.withdrawBulkMessage({
        creatorId: creatorId!,
        messageId,
      }),
    {
      enabled: !!creatorId,
      onError: (error) => handleError(error, 'Failed to withdraw mass message'),
    },
  )

  useEffect(
    () => {
      const timer = setInterval(async () => {
        withdrawMessage.refetch().then(({ data }) => {
          if (!data) {
            return clearInterval(timer)
          }

          if (data.status === 'FINISHED_WITHDRAWING') {
            return clearInterval(timer)
          }

          const normalizedNumber = _normalize(
            data.withdrawnMessages,
            data.totalMessages,
          )

          setProgress(normalizedNumber)
        })
      }, 1000)

      return () => {
        clearInterval(timer)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return useMemo(
    () => ({
      progress,
      status: withdrawMessage.data?.status,
      totalMessages: withdrawMessage.data?.totalMessages,
      withdrawnMessages: withdrawMessage.data?.withdrawnMessages,
    }),
    [progress, withdrawMessage.data],
  )
}
